import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType } from 'actions'
import { LayoutLoading } from 'components/LayoutLoading'
import { Overview } from 'components/Overview'
import { LoanFieldProps } from 'config'
import { removeNonPagePermissions } from 'hooks/hasPagePermissions'
import { usePermissions } from 'hooks/usePermissions'
import LoanAiProject from 'pages/Loan/AiProject'
import { CustomInformationPage } from 'pages/Loan/ConfigurablePage/CustomInformation'
import Fci from 'pages/Loan/Fci'
import Mers from 'pages/Loan/MERS'
import Parties from 'pages/Loan/Parties'
import ServicingBorrowerInformation from 'pages/Loan/Servicing/BorrowerInformation'
import Notes from 'pages/Loan/Servicing/Notes/Notes'
import ServicingPropertyInformation from 'pages/Loan/Servicing/PropertyInformation'
import { loanOverviewAndStepLogic } from 'pages/LoanOverview/loanOverviewAndStepLogic'
import { RelatedLoansPanel } from 'pages/LoanOverview/RelatedLoansPanel'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import { isVendorEnabled } from 'services'
import Api from 'services/api'
import { getLoanFieldSubMenus } from 'utils/loanFieldSubMenus.util'
import { setLoanNumber } from 'utils/setLoanNumber'

import ACH from './ACH'
import CollectionCalls from './CollectionCalls/CollectionCalls'
import Contacts from './Contacts/Contacts'
import CreditReporting from './CreditReporting'
import DefaultTracking from './DefaultTracking'
import Invoice from './Invoice/Invoice'
import LoanCharges from './LoanCharges/LoanCharges'
import LoanExtension from './LoanExtension/LoanExtension'
import LoanPayments from './LoanPayments'
import { applyServicingMenuLayout, buildServicingMenu } from './menuItems'
import Messages from './Messages'
import { NotePageType } from './Notes/types'
import ServicingParticipation from './Participation'
import PartyPayments from './PartyPayments'
import PaymentVouchers from './PaymentVouchers'
import Payoff from './Payoff'
import Penalties from './Penalties'
import RateChanges from './RateChanges'
import Reinstatement from './Reinstatement'
import ServicingDocument from './ServicingDocument'
import ServicingNote from './ServicingNotes/ServicingNote'
import ServicingReport from './ServicingReport/ServicingReport'
import LoanStatements from './Statements'
import TaxForms from './TaxForms/TaxForms'
import TrustLedger from './TrustLedger'
import Vouchers from './Vouchers'

export default function Servicing() {
  const [selectedMenu, setSelectedMenu] = useState('')
  const [menuItems, setMenuItems] = useState<any>({})
  const [isFciEnabled, setIsFciEnabled] = useState(false)
  const [loading, setLoading] = useState(false)

  const location = useLocation()
  const navigate = useHistory()
  const { loan_number: routeLoanNumber } = useParams<{ loan_number: string }>()

  // Moving from one serviced loan to another (global search, a loan link) keeps this component
  // mounted, so the loan has to be read from the URL rather than only at mount. Set the Api loan
  // header during render: child effects run before the parent's, and would otherwise fetch the
  // loan we just left.
  if (routeLoanNumber && Api.getLoanNumber() !== String(routeLoanNumber)) setLoanNumber(Number(routeLoanNumber))

  useEffect(() => {
    isVendorEnabled('Fci').then(setIsFciEnabled)
  }, [])

  useEffect(() => {
    // Parse robustly so extra params (e.g. ?menu=loanPayments&addPayment=1) don't corrupt the menu key.
    const menu = new URLSearchParams(location.search).get('menu') || location.search.replace('?menu=', '')
    if (menu.length) setSelectedMenu(menu)
  }, [location])

  const setMenu = (menuItem: string) => {
    const loanNumber = Api.getLoanNumber()
    navigate.push(`/servicing/${loanNumber}?menu=${menuItem}`)
  }

  useEffect(() => {
    setLoanNumber()
    const loanNumber = Api.getLoanNumber()
    loanOverviewAndStepLogic(loanNumber, '')
  }, [routeLoanNumber])

  const { auth, loanFields, menuOrder, menuVisible } = useSelector((state: any) => {
    const globalSettings = state.globalConfigReducer[GlobalConfigType.GlobalSettingV2]
    return {
      auth: state.auth,
      loanFields: state.globalConfigReducer[GlobalConfigType.LoanFields] as LoanFieldProps,
      // Admin > Configuration > Global Settings > Servicing > Servicing Menu.
      menuOrder: globalSettings?.servicingMenuOrder as string[] | undefined,
      menuVisible: globalSettings?.servicingMenuVisible as Record<string, boolean> | undefined,
    }
  })

  /**
   * Menu entries a tenant defined for itself: any loan-field section marked as a SubMenu, which in
   * practice is the Custom Information pages. They cannot be hard-coded here because the keys are
   * per tenant (`CustomInformation`, `CustomInformation-1741637163124`, ...), so they are derived
   * from the same field properties the configurable page uses. A tenant with no such section gets
   * nothing extra.
   */
  const loanFieldSubMenus = useMemo(() => getLoanFieldSubMenus(loanFields), [loanFields])

  const { hasPermission } = usePermissions()

  useEffect(() => {
    // Every entry this user has permission for, in the order the screens are built in.
    const menu = buildServicingMenu({ hasPermission, isFciEnabled, loanFieldSubMenus })

    // Safe for the derived keys: page permissions are deny-based, so a key nobody has denied is
    // allowed, and the submenu allowlist that would strip unknown keys only runs when the third
    // argument is set, which it is not here.
    removeNonPagePermissions(menu, ['pipeline', 'servicing'])

    // Admin > Configuration > Global Settings > Servicing > Servicing Menu. Tenants order this
    // menu differently because it has grown past thirty entries: some want their busiest screens
    // at the top, some want it alphabetical, some never want to see the ones they do not use. With
    // nothing saved the menu keeps the built order, which is what every tenant had before.
    const temp = applyServicingMenuLayout(menu, menuOrder, menuVisible)

    setMenuItems(temp)
    // Respect a ?menu= already in the URL (e.g. deep-linked from the pipeline);
    // only auto-select the first menu when none was requested. Read it from the
    // URL rather than `selectedMenu` state to avoid a mount-time race where the
    // stale empty state would override the requested menu.
    const requestedMenu = location.search.replace('?menu=', '')
    // Land on the menu's own first entry, whatever the admin put there - deliberately first is
    // where they want a servicer to start. It must come from the laid-out menu rather than the
    // built one, or a loan would open on a screen they have hidden.
    const landingMenu = Object.keys(temp)[0]
    if (landingMenu && !requestedMenu) setMenu(landingMenu)
  }, [auth, isFciEnabled, loanFieldSubMenus, JSON.stringify(menuOrder), JSON.stringify(menuVisible)])


  const renderFragment = useMemo(() => {
    if (loanFieldSubMenus[selectedMenu])
      return <CustomInformationPage menuKey={selectedMenu} setLoading={setLoading} />

    switch (selectedMenu) {
      case 'trustLedger':
        return <TrustLedger />
      case 'servicingDocument':
        return <ServicingDocument />
      case 'statements':
        return <LoanStatements />
      case 'taxForms':
        return <TaxForms />
      case 'servicingReport':
        return <ServicingReport />
      case 'notesForServicing':
        // Keyed apart from the global Notes screen below: both render <Notes> in the same slot, so
        // without distinct keys React reuses one instance and carries its filter, search and
        // selection state from one screen to the other.
        return <Notes key="servicing-notes" child={true} notePageType={NotePageType.SERVICING} />
      case 'globalNotes':
        return <Notes key="global-notes" child={true} />
      case 'loanExtensions':
        return <LoanExtension />
      case 'servicingNotes':
        return <ServicingNote />
      case 'rateChanges':
        return <RateChanges />
      case 'participation':
        return <ServicingParticipation />
      case 'defaultTracking':
        return <DefaultTracking />
      case 'creditReporting':
        return <CreditReporting />
      case 'collectionCalls':
        return <CollectionCalls />
      case 'textMessages':
        return <Messages />
      case 'invoice':
        return <Invoice />
      case 'payoff':
        return <Payoff />
      case 'reinstatement':
        return <Reinstatement />
      case 'loanPayments':
        return <LoanPayments />
      // Borrower Portal is a tab of Messages now; kept so a link or bookmark still naming the
      // old menu entry lands on that tab instead of an empty screen.
      case 'borrowerPortal':
        return <Messages initialTab="borrowerPortal" />
      case 'partyPayments':
        return <PartyPayments />
      case 'paymentVouchers':
        return <PaymentVouchers />
      case 'vouchers':
        return <Vouchers />
      case 'loanCharges':
        return <LoanCharges />
      case 'fci':
        return <Fci child={true} />
      case 'contacts':
        return <Contacts />
      case 'borrowerInformation':
        return <ServicingBorrowerInformation setLoading={setLoading} />
      case 'propertyInformation':
        return <ServicingPropertyInformation setLoading={setLoading} />
      case 'parties':
        return <Parties child={true} />
      case 'ach':
        return <ACH />
      case 'penalties':
        return <Penalties />
      case 'mers':
        return <Mers />
      case 'aiProject':
        return <LoanAiProject />
      default: {
        return <div></div>
      }
    }
  }, [selectedMenu, loanFieldSubMenus])

  const renderMenu = (menus: Record<string, string>) => {
    return (
      <ul className="sidebar-items flex flex-col p-4 pb-10">
        {Object.keys(menus).map((item: string, index) => {
          const isActive = selectedMenu === item
          if (item === 'tasks') {
            return (
              <li key={index} className="py-2 hover:underline cursor-pointer">
                <Link to={`/tasks/loan/${Api.getLoanNumber()}`} target="_blank">
                  <span className="flex gap-2 items-center">
                    <span>
                      {index + 1}. {menus[item]}
                    </span>
                    <ArrowTopRightOnSquareIcon className="w-4 h-4"></ArrowTopRightOnSquareIcon>
                  </span>
                </Link>
              </li>
            )
          }
          if (item === 'orderValuation') {
            return (
              <li key={index} className="py-2 hover:underline cursor-pointer">
                <Link to={`/order_valuation/${Api.getLoanNumber()}`} target="_blank">
                  <span className="flex gap-2 items-center">
                    <span>
                      {index + 1}. {menus[item]}
                    </span>
                    <ArrowTopRightOnSquareIcon className="w-4 h-4"></ArrowTopRightOnSquareIcon>
                  </span>
                </Link>
              </li>
            )
          }
          if (item === 'budget') {
            return (
              <li key={index} className="py-2 hover:underline cursor-pointer">
                <Link to={`/budget/${Api.getLoanNumber()}`} target="_blank">
                  <span className="flex gap-2 items-center">
                    <span>
                      {index + 1}. {menus[item]}
                    </span>
                    <ArrowTopRightOnSquareIcon className="w-4 h-4"></ArrowTopRightOnSquareIcon>
                  </span>
                </Link>
              </li>
            )
          }
          return (
            <li
              key={index}
              onClick={() => {
                setMenu(item)
              }}
              className="py-2"
            >
              <p className={`hover:underline cursor-pointer ${isActive ? 'border px-4 py-1 bg-zinc-100' : 'py-1'}`}>
                {index + 1}. {menus[item]}
              </p>
            </li>
          )
        })}
      </ul>
    )
  }

  return (
    <div className="py-6 px-2">
      {/* Same panel, and the same spot inside the header card, as the origination Loan Overview.
          A servicer working a file wants the borrower's other loans, and which of them is in
          default, without leaving the screen. */}
      <Overview
        title="Servicing"
        beforeHeader={<RelatedLoansPanel loanNumber={routeLoanNumber} />}
        showAuditLog={true}
      />
      <div className="max-w-screen-2xl m-auto grid grid-cols-12 md:gap-6">
        <div className="sidebar-left col-span-12 md:col-span-3 shrink-0 bg-white shadow1 rounded mb-4">
          {renderMenu(menuItems)}
        </div>
        <div
          // Keyed by loan so the open screen remounts (and refetches) when the route switches
          // loans while the menu selection stays the same.
          key={routeLoanNumber}
          className="relative h-fit content-right col-span-12 md:col-span-9 bg-white p-4 md:p-7 rounded shadow1 mb-4"
        >
          <LayoutLoading show={loading} />
          {renderFragment}
        </div>
      </div>
    </div>
  )
}
